<script>
import { mapActions, mapGetters } from 'vuex'
import { getLocationFromSlug } from '@utils/contentful'
import axios from 'axios'
import config from '@src/config'

import Layout from '@layouts/main.vue'

export default {
  page: {
    title: 'Order',
    meta: [{ name: 'description', content: 'The Order page.' }],
  },
  components: {
    Layout,
  },
  data: () => ({
    loading: true,
    location: {},
    orderLink: null,
  }),
  computed: {
    ...mapGetters('contentful', {
      brand: 'getBrand',
      content: 'getContent',
      contentIsRetrieved: 'getContentIsRetrieved',
    }),
    ...mapGetters('location', {
      isWantToOrderPromptVisible: 'isWantToOrderPromptVisible',
    }),
    orderBaseUrl() {
      return this.brand?.oatBaseUrl || this.content?.oatBaseUrl
    },
    orderLinkText() {
      return this.location?.oatLinkText || 'Order'
    },
    showOrderLink() {
      const { slug } = this
      const isEnabled = this.location?.showOatLink === 'Show'

      return slug && isEnabled
    },
    slug() {
      return this.$route.params.restaurant
    },
  },
  watch: {
    contentIsRetrieved: {
      immediate: true,
      handler(newVal) {
        if (newVal === true) {
          this.init()
        }
      },
    },
  },
  methods: {
    ...mapActions('ui', {
      showError: 'showError',
    }),
    async checkOpenAccount(tableNumber) {
      const { salesAreaId, siteId, merchantId } = this.location || {}

      if (!salesAreaId || !siteId) {
        return false
      }

      try {
        await axios.post(
          `${config.apiEndpoint}site/${siteId}/area/${salesAreaId}/table/${tableNumber}`,
          { merchantId }
        )
      } catch (err) {
        // API responds with 500 if no account found (valid/invalid table number)
        return false
      }

      return true
    },
    async init() {
      const { checkOpenAccount, orderBaseUrl, slug } = this
      const table = this.$route.query.table

      // get location data from Contentful
      try {
        this.location = await getLocationFromSlug(slug)
      } catch (err) {
        // push to homepage if no location found
        this.$router.replace('/')
        this.showError('There was a problem fetching the location')
        return
      }

      const hasOpenAccount = await checkOpenAccount(table)

      // push to pay route if table already has an open account
      if (hasOpenAccount) {
        this.$router.replace(`/${slug}/table/${table}`)
        return
      }

      // dependent on the base URL being added to contentful
      if (!orderBaseUrl) {
        this.$router.replace('/')
        return
      }

      // building the URL for the relevant location/table
      this.orderLink = `${orderBaseUrl}${slug}/order`

      // add table number query
      if (table) {
        this.orderLink = `${this.orderLink}?table=${table}`
      }

      this.loading = false
    },
  },
}
</script>

<template>
  <Layout screen="bill" :loading="loading">
    <main :class="$style.page">
      <h3> The Bill </h3>
      <p :class="$style.description">You have no items</p>
    </main>

    <div
      v-if="isWantToOrderPromptVisible && orderLink && showOrderLink"
      class="footer"
      :class="$style.footer"
    >
      <h4 :class="$style.footerText">Want to order?</h4>
      <a :href="orderLink">
        <BaseButton v-text="orderLinkText" />
      </a>
    </div>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';

.page {
  flex: 1;
  text-align: center;
  max-width: 280px;
  margin: auto;
  padding: 3rem 0;
}

.description {
  font-family: $content-font-family;
  font-weight: $content-font-weight;
}

.error {
  width: 70%;
  margin: 2rem auto;
}

.footer {
  border-top: 2px dashed var(--color-dark-grey);
  height: 8.4375rem;
  width: 100%;
}

.footerText {
  font-size: 1rem;
  margin-bottom: 1rem;
}
</style>
